import { InitPageStrict } from "../index";
import * as Toastr from "toastr";

declare var $: any;

class GeneralSettingsPage {
    private form: any;
    private modal: any;
    private viewOnly = false;
    private baseline: { [name: string]: string } = {};
    private authRequest: any = null;
    private saving = false;
    private pendingAction: any = null;
    private authDefaults: { title: string; text: string; label: string } = { title: "", text: "", label: "" };

    init() {
        this.form = $("#gs-form");
        if (!this.form.length) return;
        this.modal = $("#gs-auth-modal");
        this.viewOnly = String(this.form.data("view-only")) === "true";

        this.initMultiSelects();
        this.updateDependents(false);
        this.baseline = this.snapshot();

        this.form.on("change input", "input:not(.gs-row-all):not(.gs-col-all), select", () => {
            this.updateDependents(true);
            this.refresh();
        });
        this.form.on("submit", (e: any) => e.preventDefault());
        this.form.on("click", ".gs-action", (e: any) => this.requestAction($(e.currentTarget)));
        $("#gs-discard").on("click", () => this.discard());
        $("#gs-save").on("click", () => this.requestSave());
        $("#gs-run-transfer").on("click", () => {
            if (!this.viewOnly) $("#gs-run-modal").modal("show");
        });
        $("#gs-run-confirm").on("click", (e: any) => this.runTransfer($(e.currentTarget)));
        this.initTabs();
        this.initInfoPopovers();
        this.initAuthModal();
        this.initExportModal();
        $("#gs-rules-clear").on("click", () => {
            this.form.find("input.gs-rule:checked:enabled").prop("checked", false);
            this.refresh();
        });
        this.initRulesMatrix();
        this.refresh();
        this.showFlashMessages();
    }

    showFlashMessages() {
        const messages = this.form.data("flash-messages") || {};
        const options = { escapeHtml: true };
        if (messages.success) Toastr.success(messages.success, undefined, options);
        if (messages.warning) Toastr.warning(messages.warning, undefined, options);
        if (messages.error) Toastr.error(messages.error, undefined, options);
    }

    snapshot() {
        const values: { [name: string]: string } = {};
        this.form.find("input.gs-input, input[type=number], input.gs-rule, select.gs-input").each((_: number, el: HTMLInputElement) => {
            if (el.type === "select-multiple") values[el.id] = JSON.stringify($(el).val() || []);
            else values[el.id] = el.type === "checkbox" ? String(el.checked) : el.value;
        });
        return values;
    }

    dirtyCount() {
        const current = this.snapshot();
        return Object.keys(current).filter((key) => current[key] !== this.baseline[key]).length;
    }

    isDirty() {
        return this.dirtyCount() > 0;
    }

    initMultiSelects() {
        const multis = this.form.find("select.gs-multi");
        if (!multis.length) return;
        multis.each((_: number, el: HTMLElement) => this.renderMulti($(el)));
        this.form.on("change", ".gs-add", (e: any) => {
            const add = $(e.currentTarget);
            const select = add.closest(".gs-multi-box").prev("select.gs-multi");
            select.find("option").filter((_: number, o: HTMLOptionElement) => o.value === add.val()).prop("selected", true);
            this.renderMulti(select);
            select.next(".gs-multi-box").find(".gs-add").trigger("focus");
        });
        this.form.on("click", ".gs-chip button", (e: any) => {
            const button = $(e.currentTarget);
            const select = button.closest(".gs-multi-box").prev("select.gs-multi");
            select.find("option").filter((_: number, o: HTMLOptionElement) => o.value === String(button.data("value"))).prop("selected", false);
            this.renderMulti(select);
            this.refresh();
        });
    }

    renderMulti(select: any) {
        const locked = String(select.data("locked-status-id") || "");
        const disabled = select.prop("disabled");
        const options = select.find("option").get() as HTMLOptionElement[];
        const box = $("<div>", { class: `gs-multi-box${disabled ? " disabled" : ""}` });

        options.filter((o) => o.selected).forEach((o) => {
            const chip = $("<span>", { class: "gs-chip" }).text(o.text);
            if (o.value === locked) chip.addClass("locked").attr("title", o.title);
            else if (!disabled) chip.append($("<button>", { type: "button", "aria-label": `Remove ${o.text}`, "data-value": o.value }).text("×"));
            box.append(chip);
        });

        const left = options.filter((o) => !o.selected && !o.disabled);
        if (!disabled && left.length) {
            const add = $("<select>", { class: "gs-add", "aria-label": select.data("placeholder") });
            add.append($("<option>", { value: "" }).text(select.data("placeholder")));
            left.forEach((o) => add.append($("<option>", { value: o.value }).text(o.text)));
            box.append(add);
        } else if (disabled && !box.children().length) {
            box.append($("<span>", { class: "gs-hint" }).text("None"));
        }

        select.next(".gs-multi-box").remove();
        select.after(box);
    }

    updateDependents(clearOff: boolean) {
        this.form.find("[data-gs-show-with]").each((_: number, el: HTMLElement) => {
            $(el).prop("hidden", !$(`#${$(el).data("gs-show-with")}`).prop("checked"));
        });
        this.form.find("[data-gs-note-with]").each((_: number, el: HTMLElement) => {
            const on = $(`#${$(el).data("gs-note-with")}`).prop("checked");
            $(el).prop("hidden", on).closest(".gs-row").toggleClass("blocked", !on);
        });
        if (this.viewOnly) return;
        this.form.find("[data-gs-enable-with]").each((_: number, el: HTMLElement) => {
            const on = $(`#${$(el).data("gs-enable-with")}`).prop("checked");
            const changed = $(el).prop("disabled") === on;
            $(el).prop("disabled", !on);
            if (changed && $(el).is("select.gs-multi")) this.renderMulti($(el));
            if (!on && clearOff && $(el).data("gs-clear-off")) $(el).prop("checked", false);
        });
    }

    refresh() {
        this.updateRules();
        const dirty = this.isDirty();
        this.setStatus(this.saving ? "saving" : dirty ? "dirty" : "clean");
        $("#gs-discard").prop("disabled", !dirty || this.saving);
        $("#gs-save").prop("disabled", !dirty || this.saving || this.viewOnly);
        if (dirty) this.validateThreshold(false);
    }

    updateRules() {
        const section = $("#gs-transfer-rules");
        if (!section.length) return;
        const off = !$("#general_setting_automatic_transfer").prop("checked");
        section.prop("hidden", off);
        $('#gs-tabs a[href="#gs-transfer-rules"]').prop("hidden", off);

        section.find("tr[data-from]").each((_: number, row: HTMLElement) => {
            const boxes = $(row).find("input.gs-rule");
            const targets = boxes.filter(":checked").map((_i: number, el: HTMLInputElement) => el.value).get();
            $(row).find(".gs-row-summary")
                .toggleClass("text-success", targets.length > 0)
                .toggleClass("text-muted", targets.length === 0)
                .attr("title", targets.length ? `${section.data("prefix")} ${targets.join(", ")}` : section.data("none"))
                .text(`${targets.length} ${section.data("of")} ${boxes.length}`);
            this.setAllState($(row).find("input.gs-row-all"), boxes);
        });
        section.find("input.gs-col-all").each((_: number, el: HTMLElement) => {
            this.setAllState($(el), section.find(`input.gs-rule[value="${$(el).data("to")}"]`));
        });

        const allowed = section.find("input.gs-rule:checked").length;
        const count = $("#gs-rules-count");
        count.empty().append($("<b>").text(`${allowed} ${count.data("of")} ${count.data("total")}`), document.createTextNode(` ${count.data("suffix")}`));
        $("#gs-rules-clear").prop("disabled", allowed === 0 || this.viewOnly || this.saving);
    }

    initRulesMatrix() {
        const section = $("#gs-transfer-rules");
        if (!section.length) return;
        section.find("input.gs-row-all").on("change", (e: any) => {
            const all = $(e.currentTarget);
            all.closest("tr").find("input.gs-rule:enabled").prop("checked", all.prop("checked"));
            this.refresh();
        });
        section.find("input.gs-col-all").on("change", (e: any) => {
            const all = $(e.currentTarget);
            section.find(`input.gs-rule[value="${all.data("to")}"]:enabled`).prop("checked", all.prop("checked"));
            this.refresh();
        });

        const table = section.find("table.gs-matrix");
        table.on("mouseenter", "td.gs-cell", (e: any) => {
            table.find(`tr > :nth-child(${e.currentTarget.cellIndex + 1})`).addClass("gs-col-hover");
        });
        table.on("mouseleave", "td.gs-cell", () => table.find(".gs-col-hover").removeClass("gs-col-hover"));
    }

    setAllState(all: any, boxes: any) {
        const checked = boxes.filter(":checked").length;
        all.prop("checked", boxes.length > 0 && checked === boxes.length)
            .prop("indeterminate", checked > 0 && checked < boxes.length);
    }

    setStatus(state: string) {
        const status = $("#gs-status");
        status.removeClass("dirty saving").addClass(state === "clean" ? "" : state);
        let text = status.data(state);
        if (state === "dirty" && status.data("dirty-one")) {
            const count = this.dirtyCount();
            text = `${count} ${status.data(count === 1 ? "dirty-one" : "dirty-many")}`;
        }
        status.find(".gs-status-text").text(text);
    }

    discard() {
        Object.keys(this.baseline).forEach((id) => {
            const el = document.getElementById(id) as HTMLInputElement;
            if (!el) return;
            if (el.type === "checkbox") el.checked = this.baseline[id] === "true";
            else if (el.type === "select-multiple") { $(el).val(JSON.parse(this.baseline[id])); this.renderMulti($(el)); }
            else el.value = this.baseline[id];
        });
        $(".gs-threshold").removeClass("has-error");
        this.updateDependents(false);
        this.refresh();
    }

    validateThreshold(focus: boolean) {
        const input = $("#general_setting_antenna_health_threshold");
        if (!input.length) return true;
        const value = String(input.val() || "").trim();
        const valid = /^\d+$/.test(value);
        input.closest(".gs-threshold").toggleClass("has-error", !valid);
        input.attr("aria-invalid", valid ? "false" : "true");
        if (!valid && focus) {
            input.trigger("focus");
            Toastr.error("Enter the antenna health alert threshold as a whole number of minutes.");
        }
        return valid;
    }

    requestSave() {
        if (this.viewOnly || this.saving || !this.isDirty()) return;
        if (!this.validateThreshold(true)) return;
        this.resetAuth();
        this.modal.modal("show");
    }

    requestAction(button: any) {
        if (this.viewOnly || this.saving || button.prop("disabled")) return;
        this.resetAuth();
        const altSerials = button.data("alt-serials") && $(button.data("alt-serials")).prop("checked");
        this.pendingAction = {
            url: button.data("url") + (altSerials ? "?alt_serials=true" : ""),
            authUrl: button.data("auth-url")
        };
        this.modal.toggleClass("gs-dialog-danger", !!button.data("danger"));
        this.modal.find("#gs-auth-submit").toggleClass("gs-btn-danger", !!button.data("danger-button"));
        this.modal.find("#gs-auth-title").text(button.data("title"));
        this.modal.find("#gs-auth-text").text(altSerials ? `${button.data("text")} ${button.data("alt-text")}` : button.data("text"));
        this.modal.find("#gs-auth-submit").data("label", button.data("label")).find(".gs-btn-label").text(button.data("label"));
        this.modal.modal("show");
    }

    submitAction(url: string) {
        $("<form>", { method: "POST", action: url })
            .append($("<input>", { type: "hidden", name: "authenticity_token", value: $('meta[name="csrf-token"]').attr("content") }))
            .appendTo("body")
            .trigger("submit");
    }

    save() {
        const button = $("#gs-save");
        const sent = this.snapshot();
        this.saving = true;
        this.setBusy(button, true);
        this.refresh();
        $.ajax({
            method: "POST",
            url: this.form.attr("action"),
            dataType: "json",
            data: this.form.serialize()
        })
            .done((result: any) => {
                this.baseline = sent;
                Toastr.success(result && result.message ? result.message : "General Settings have been saved.");
            })
            .fail((xhr: any) => {
                const message = xhr.responseJSON && xhr.responseJSON.message;
                Toastr.error(message || "Couldn’t save General Settings. Try again.");
            })
            .always(() => {
                this.saving = false;
                this.setBusy(button, false);
                this.refresh();
            });
    }

    runTransfer(button: any) {
        if (this.viewOnly || button.prop("disabled")) return;
        this.setBusy(button, true);
        button.prop("disabled", true);
        $.ajax({
            method: "POST",
            url: $("#gs-run-transfer").data("url"),
            dataType: "json",
            headers: { "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content") }
        })
            .done((result: any) => Toastr.success(result.message))
            .fail((xhr: any) => {
                const message = xhr.responseJSON && xhr.responseJSON.message;
                Toastr.error(message || "Couldn’t start the automatic transfer. Try again.");
            })
            .always(() => {
                this.setBusy(button, false);
                button.prop("disabled", false);
                $("#gs-run-modal").modal("hide");
            });
    }

    initExportModal() {
        const modal = $("#gs-export-modal");
        const input = modal.find("#gs-export-email");
        const initial = String(input.val() || "");
        let request: any = null;

        $("#gs-export-inventory").on("click", () => modal.modal("show"));
        modal.on("shown.bs.modal", () => input.trigger("focus").trigger("select"));
        modal.on("hide.bs.modal", () => {
            if (request) request.abort();
        });
        modal.on("hidden.bs.modal", () => {
            input.val(initial);
            this.setFieldError(modal.find("#gs-export-hint"), input, null);
        });
        input.on("input", () => {
            if (input.closest(".gs-dialog-field").hasClass("err")) this.setFieldError(modal.find("#gs-export-hint"), input, null);
        });
        modal.on("submit", "#gs-export-form", (e: any) => {
            e.preventDefault();
            const form = $(e.currentTarget);
            const button = modal.find("#gs-export-submit");
            const hint = modal.find("#gs-export-hint");
            const email = String(input.val() || "").trim();
            if (button.prop("disabled")) return;
            if (!email || !(input[0] as HTMLInputElement).checkValidity()) {
                this.setFieldError(hint, input, email ? "invalid" : "empty");
                input.trigger("focus");
                return;
            }

            this.setFieldError(hint, input, null);
            button.prop("disabled", true);
            this.setBusy(button, true);
            request = $.ajax({ method: "GET", url: form.attr("action"), dataType: "json", data: form.serialize() })
                .done((result: any) => {
                    if (result && result.status === "success") {
                        modal.modal("hide");
                        Toastr.success(result.message);
                    } else {
                        this.setFieldError(hint, input, "failed");
                    }
                })
                .fail((xhr: any) => {
                    if (xhr.statusText !== "abort") this.setFieldError(hint, input, "failed");
                })
                .always(() => {
                    request = null;
                    button.prop("disabled", false);
                    this.setBusy(button, false);
                });
        });
    }

    setFieldError(hint: any, input: any, key: string | null) {
        input.closest(".gs-dialog-field").toggleClass("err", !!key);
        input.attr("aria-invalid", key ? "true" : "false");
        hint.toggleClass("error", !!key).attr("role", key ? "alert" : null);
        hint.find(".gs-dialog-hint-text").text(hint.data(key || "hint"));
    }

    setBusy(button: any, busy: boolean) {
        button.toggleClass("busy", busy).attr("aria-busy", busy ? "true" : null);
        button.find(".gs-btn-label").text(button.data(busy ? "busy-label" : "label"));
    }

    initTabs() {
        const links = $("#gs-tabs a");
        links.on("click", (e: any) => {
            e.preventDefault();
            const target = document.querySelector($(e.currentTarget).attr("href"));
            if (!target) return;
            links.removeClass("active").removeAttr("aria-current");
            $(e.currentTarget).addClass("active").attr("aria-current", "true");
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        });

        if (!("IntersectionObserver" in window)) return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                links.removeClass("active").removeAttr("aria-current");
                links.filter(`[href="#${entry.target.id}"]`).addClass("active").attr("aria-current", "true");
            });
        }, { rootMargin: "-80px 0px -60% 0px" });
        $(".general-settings-page .gs-section").each((_: number, el: Element) => observer.observe(el));
    }

    initInfoPopovers() {
        const triggers = $(".general-settings-page .info-popover");
        const closeAll = (except?: any) => {
            triggers.each((_: number, el: HTMLElement) => {
                if (except && except[0] === el) return;
                if ($(el).attr("aria-expanded") === "true") $(el).attr("aria-expanded", "false").popover("hide");
            });
        };

        triggers.popover({
            trigger: "manual",
            placement: "auto top",
            container: "body",
            template: '<div class="popover gs-info-pop" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>'
        });

        triggers.on("click", (e: any) => {
            e.preventDefault();
            const trigger = $(e.currentTarget);
            const open = trigger.attr("aria-expanded") === "true";
            closeAll(trigger);
            trigger.attr("aria-expanded", String(!open)).popover(open ? "hide" : "show");
        });

        $(document).on("click.gsPopover", (e: any) => {
            if ($(e.target).closest(".info-popover, .gs-info-pop").length) return;
            closeAll();
        });
        $(document).on("keydown.gsPopover", (e: any) => {
            if (e.key !== "Escape") return;
            const open = triggers.filter('[aria-expanded="true"]');
            if (!open.length) return;
            closeAll();
            open.trigger("focus");
        });
        $(window).on("resize.gsPopover", () => closeAll());
    }

    initAuthModal() {
        this.authDefaults = {
            title: this.modal.find("#gs-auth-title").text(),
            text: this.modal.find("#gs-auth-text").text(),
            label: this.modal.find("#gs-auth-submit").data("label")
        };
        this.modal.on("click", "#gs-auth-toggle", () => {
            this.setShown(this.authInput().attr("type") === "password");
            this.authInput().trigger("focus");
        });
        this.modal.on("input", "#gs-auth-password", () => {
            if (this.modal.find(".gs-dialog-field").hasClass("err")) this.setAuthError(null);
        });
        this.modal.on("submit", "#gs-auth-form", (e: any) => {
            e.preventDefault();
            this.submitAuth();
        });
        this.modal.on("shown.bs.modal", () => this.authInput().trigger("focus"));
        this.modal.on("hide.bs.modal", () => {
            if (this.authRequest) this.authRequest.abort();
        });
        this.modal.on("hidden.bs.modal", () => this.resetAuth());
    }

    authInput() {
        return this.modal.find("#gs-auth-password");
    }

    resetAuth() {
        this.pendingAction = null;
        this.modal.removeClass("gs-dialog-danger");
        this.modal.find("#gs-auth-title").text(this.authDefaults.title);
        this.modal.find("#gs-auth-text").text(this.authDefaults.text);
        this.modal.find("#gs-auth-submit").removeClass("gs-btn-danger");
        this.modal.find("#gs-auth-submit").data("label", this.authDefaults.label);
        this.setBusy(this.modal.find("#gs-auth-submit").prop("disabled", false), false);
        this.setAuthError(null);
        this.setShown(false);
        this.authInput().val("");
    }

    setShown(shown: boolean) {
        const eye = this.modal.find("#gs-auth-toggle");
        this.authInput().attr("type", shown ? "text" : "password");
        eye.toggleClass("shown", shown)
            .attr("aria-pressed", String(shown))
            .attr("aria-label", eye.data(shown ? "hide-label" : "show-label"));
    }

    setAuthError(key: string | null) {
        this.setFieldError(this.modal.find("#gs-auth-hint"), this.authInput(), key);
    }

    shake() {
        const field = this.modal.find(".gs-dialog-field");
        field.removeClass("shake");
        void field[0].offsetWidth;
        field.addClass("shake");
        this.authInput().trigger("select");
    }

    submitAuth() {
        const button = this.modal.find("#gs-auth-submit");
        const password = String(this.authInput().val() || "");
        if (button.prop("disabled")) return;
        if (!password) {
            this.setAuthError("empty");
            this.authInput().trigger("focus");
            return;
        }

        this.setAuthError(null);
        button.prop("disabled", true);
        this.setBusy(button, true);
        const action = this.pendingAction;
        this.authRequest = $.ajax({
            method: "POST",
            dataType: "json",
            url: action ? action.authUrl : this.form.data("auth-url"),
            headers: { "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content") },
            data: { password }
        })
            .done((result: any) => {
                if (result && result.status === true) {
                    this.modal.modal("hide");
                    if (action) this.submitAction(action.url);
                    else this.save();
                } else {
                    this.setAuthError("wrong");
                    this.shake();
                }
            })
            .fail((xhr: any) => {
                if (xhr.statusText !== "abort") this.setAuthError("failed");
            })
            .always(() => {
                this.authRequest = null;
                button.prop("disabled", false);
                this.setBusy(button, false);
            });
    }
}

export function LoadPage() {
    InitPageStrict("general_settings edit_general_settings", () => {
        new GeneralSettingsPage().init();
    });
    InitPageStrict("general_settings edit", () => {
        new GeneralSettingsPage().init();
    });
}
